/**
 * The rect the card should anchor to. An inline citation broken across lines has one
 * client rect per line fragment, but getBoundingClientRect() is their union — a box
 * spanning the full column — so anchoring to it centers the card on neither fragment.
 * Anchor to the line under the pointer instead; without a pointer (keyboard focus),
 * the first line.
 */
export function pickCitationAnchorRect(element: HTMLElement, pointerY: number | null): DOMRect {
  const lines = groupClientRectsIntoLines(element.getClientRects())
  if (lines.length === 0) return element.getBoundingClientRect()

  if (pointerY !== null) {
    const hovered = lines.find((line) => pointerY >= line.top && pointerY <= line.bottom)
    if (hovered) return hovered
  }
  return lines[0]
}

/**
 * getClientRects() splits an inline element's boxes at child-element boundaries as
 * well as at line breaks — an italicized case name yields two rects on one visual
 * line — so rects sharing a line must merge into one before a line is picked, or the
 * card would center on the first sub-rect (e.g. just the case name).
 */
function groupClientRectsIntoLines(rectList: DOMRectList): DOMRect[] {
  const rects = Array.from(rectList).filter((rect) => rect.width > 0 && rect.height > 0)

  const lines: { top: number; bottom: number; left: number; right: number }[] = []
  for (const rect of rects) {
    const line = lines.find((candidate) => rect.top < candidate.bottom && rect.bottom > candidate.top)
    if (!line) {
      lines.push({ top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right })
      continue
    }
    line.top = Math.min(line.top, rect.top)
    line.bottom = Math.max(line.bottom, rect.bottom)
    line.left = Math.min(line.left, rect.left)
    line.right = Math.max(line.right, rect.right)
  }

  return lines.map((line) => new DOMRect(line.left, line.top, line.right - line.left, line.bottom - line.top))
}
